/**
 * Smart API Router для Mini App
 * Автоматически выбирает лучший API endpoint с failover
 * Работает параллельно со старой системой
 */

const API_ENDPOINTS = {
  // Primary - основной K3s сервер (write operations)
  primary: process.env.REACT_APP_API_PRIMARY || 'https://vpn-admin.shurik.ch',
  
  // Replica - VDS Mirror с API Proxy (форвардит все /api/* на K8s)
  // NOTE: Это НЕ true read-replica, а proxy для обхода блокировок
  replica: process.env.REACT_APP_API_REPLICA || 'https://d5dm6fphh1c2fi99l6kt.avjje9e3.apigw.yandexcloud.net',
  
  // Legacy - старая система (fallback, не используется)
  legacy: 'https://vpn-admin.shurik.ch/static/telegram-miniapp'
};

// Feature flag для включения smart routing
const SMART_ROUTING_ENABLED = 
  process.env.REACT_APP_ENABLE_SMART_ROUTER === 'true' ||
  new URLSearchParams(window.location.search).get('smart') === '1';

class SmartAPIClient {
  constructor() {
    this.preferredEndpoint = this.loadPreferred();
    this.failureCount = new Map();
    this.latencyStats = new Map();
    this.circuitBreaker = new Map(); // endpoint -> { failures, lastFailure, isOpen }
    
    // Circuit breaker config
    this.CIRCUIT_BREAKER_THRESHOLD = 5; // failures
    this.CIRCUIT_BREAKER_TIMEOUT = 60000; // 60 seconds
    this.MAX_RETRIES = 2;
    
    console.log('[SmartAPI] Initialized', {
      smartRoutingEnabled: SMART_ROUTING_ENABLED,
      preferredEndpoint: this.preferredEndpoint,
      endpoints: API_ENDPOINTS
    });
  }

  /**
   * Главный метод для API запросов
   * Возвращает Response объект (совместимо с fetch API)
   */
  async request(path, options = {}) {
    if (!SMART_ROUTING_ENABLED) {
      // Legacy режим - используем только primary
      return this.fetchWithTimeout(`${API_ENDPOINTS.primary}${path}`, options);
    }

    const method = options.method || 'GET';
    const isReadOnly = this.isReadOnlyRequest(method, path);
    
    console.log('[SmartAPI] Request', { path, method, isReadOnly });
    
    // Определяем порядок эндпоинтов
    const endpoints = this.getEndpointPriority(isReadOnly);
    
    for (let i = 0; i < endpoints.length; i++) {
      const endpoint = endpoints[i];
      
      // Проверяем circuit breaker
      if (this.isCircuitOpen(endpoint)) {
        console.log(`[SmartAPI] Circuit breaker open for ${endpoint}, skipping`);
        continue;
      }
      
      try {
        const startTime = Date.now();
        const url = `${endpoint}${path}`;
        
        console.log(`[SmartAPI] Trying ${endpoint} (attempt ${i + 1}/${endpoints.length})`);
        
        const response = await this.fetchWithTimeout(url, options);
        const latency = Date.now() - startTime;
        
        // ВАЖНО: Не бросаем ошибку для 4xx статусов - это валидные API ответы!
        // Ошибкой считаем только 5xx (сервер недоступен) или сетевые ошибки
        // 400 Bad Request, 401 Unauthorized, 403 Forbidden - это нормальные ответы API
        if (response.status >= 500) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        // Успех (включая 4xx - это валидные ответы от API)!
        this.recordSuccess(endpoint, latency);
        console.log(`[SmartAPI] Success via ${endpoint} (${latency}ms)`);
        
        return response;
        
      } catch (error) {
        console.warn(`[SmartAPI] Failed ${endpoint}:`, error.message);
        this.recordFailure(endpoint);
        
        // Если это последний endpoint - выбрасываем ошибку
        if (i === endpoints.length - 1) {
          throw new Error(`All API endpoints failed. Last error: ${error.message}`);
        }
        
        // Пробуем следующий endpoint
        continue;
      }
    }
    
    // Если дошли сюда - все endpoints failed
    throw new Error('All API endpoints are unavailable');
  }

  /**
   * Определяет приоритет endpoints в зависимости от операции
   */
  getEndpointPriority(isReadOnly) {
    if (isReadOnly) {
      // Read операции: сначала replica (быстрее), потом primary
      return [API_ENDPOINTS.replica, API_ENDPOINTS.primary];
    } else {
      // Write операции: только primary
      return [API_ENDPOINTS.primary];
    }
  }

  /**
   * Определяет, является ли запрос read-only
   */
  isReadOnlyRequest(method, path) {
    // GET запросы всегда read-only
    if (method === 'GET' || method === 'HEAD') {
      return true;
    }
    
    // Write операции (всегда идут на primary):
    // - /api/telegram/auth - аутентификация (создание/обновление сессий)
    // - /api/telegram/renew - продление подписки
    // - /api/telegram/apply-promo - активация промокода
    // Все остальные POST запросы считаем read-only и пробуем через replica
    
    const writeOnlyPaths = [
      '/api/telegram/auth',
      '/api/telegram/renew',
      '/api/telegram/apply-promo',
      '/api/telegram/stars/sync-payment',
    ];
    
    return !writeOnlyPaths.some(p => path.includes(p));
  }

  /**
   * Fetch с таймаутом и обработкой ошибок
   * Возвращает Response объект (для совместимости с существующим кодом)
   */
  async fetchWithTimeout(url, options = {}, timeout = 15000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          // НЕ устанавливаем Content-Type по умолчанию, пусть caller контролирует
          ...options.headers,
        },
      });
      
      clearTimeout(timeoutId);
      
      // Возвращаем Response объект (не парсим JSON здесь)
      // Caller сам решит, как обрабатывать (response.json(), response.text(), etc.)
      return response;
      
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      
      throw error;
    }
  }

  /**
   * Circuit Breaker: проверка открыт ли circuit
   */
  isCircuitOpen(endpoint) {
    const breaker = this.circuitBreaker.get(endpoint);
    
    if (!breaker || !breaker.isOpen) {
      return false;
    }
    
    // Проверяем истек ли timeout
    const timeSinceFailure = Date.now() - breaker.lastFailure;
    if (timeSinceFailure > this.CIRCUIT_BREAKER_TIMEOUT) {
      // Пробуем закрыть circuit
      console.log(`[SmartAPI] Circuit breaker timeout expired for ${endpoint}, trying again`);
      breaker.isOpen = false;
      breaker.failures = 0;
      return false;
    }
    
    return true;
  }

  /**
   * Запись успешного запроса
   */
  recordSuccess(endpoint, latency) {
    // Сохраняем предпочтительный endpoint
    this.preferredEndpoint = endpoint;
    this.savePreferred(endpoint);
    
    // Сбрасываем счетчик ошибок
    this.failureCount.set(endpoint, 0);
    
    // Закрываем circuit breaker если был открыт
    const breaker = this.circuitBreaker.get(endpoint);
    if (breaker) {
      breaker.isOpen = false;
      breaker.failures = 0;
    }
    
    // Записываем латентность
    const stats = this.latencyStats.get(endpoint) || [];
    stats.push(latency);
    if (stats.length > 10) stats.shift(); // Keep last 10
    this.latencyStats.set(endpoint, stats);
  }

  /**
   * Запись ошибки
   */
  recordFailure(endpoint) {
    const failures = (this.failureCount.get(endpoint) || 0) + 1;
    this.failureCount.set(endpoint, failures);
    
    // Circuit breaker logic
    let breaker = this.circuitBreaker.get(endpoint);
    if (!breaker) {
      breaker = { failures: 0, lastFailure: 0, isOpen: false };
      this.circuitBreaker.set(endpoint, breaker);
    }
    
    breaker.failures++;
    breaker.lastFailure = Date.now();
    
    if (breaker.failures >= this.CIRCUIT_BREAKER_THRESHOLD) {
      breaker.isOpen = true;
      console.warn(`[SmartAPI] Circuit breaker OPENED for ${endpoint} after ${breaker.failures} failures`);
    }
  }

  /**
   * Получение статистики
   */
  getStats() {
    const stats = {};
    
    for (const [endpoint, latencies] of this.latencyStats.entries()) {
      const avg = latencies.reduce((a, b) => a + b, 0) / latencies.length;
      const failures = this.failureCount.get(endpoint) || 0;
      const breaker = this.circuitBreaker.get(endpoint);
      
      stats[endpoint] = {
        avgLatency: Math.round(avg),
        failures: failures,
        circuitOpen: breaker?.isOpen || false,
        isPreferred: endpoint === this.preferredEndpoint
      };
    }
    
    return stats;
  }

  /**
   * LocalStorage для сохранения предпочтительного endpoint
   */
  loadPreferred() {
    try {
      return localStorage.getItem('api_preferred_endpoint') || API_ENDPOINTS.primary;
    } catch {
      return API_ENDPOINTS.primary;
    }
  }

  savePreferred(endpoint) {
    try {
      localStorage.setItem('api_preferred_endpoint', endpoint);
    } catch {
      // Ignore localStorage errors
    }
  }

  /**
   * Сброс статистики (для тестирования)
   */
  reset() {
    this.failureCount.clear();
    this.latencyStats.clear();
    this.circuitBreaker.clear();
    this.preferredEndpoint = API_ENDPOINTS.primary;
    console.log('[SmartAPI] Stats reset');
  }
}

// Singleton instance
export const smartAPI = new SmartAPIClient();

// Export для тестирования
export { API_ENDPOINTS, SMART_ROUTING_ENABLED };

// Debug endpoint
if (process.env.NODE_ENV === 'development') {
  window.smartAPI = smartAPI;
  console.log('[SmartAPI] Debug mode: window.smartAPI available');
}
